import { useState } from 'react';
import { Upload, Download, Trash2, FileText } from 'lucide-react';
import type { AssetDocumentType } from '../api';
import { useDocumentos, useUploadDocumento, useDownloadDocumento, useExcluirDocumento } from '../hooks';
import { formatarData, formatarTamanho } from '../utils/formatters';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  assetId: string;
  canWrite: boolean;
}

const DOCUMENT_TYPE_LABELS: Record<AssetDocumentType, string> = {
  invoice: 'Nota fiscal',
  responsibility_term: 'Termo de responsabilidade',
  warranty: 'Garantia',
  rental_contract: 'Contrato de locação',
  technical_report: 'Laudo técnico',
  maintenance_order: 'Ordem de manutenção',
  other: 'Outro',
};

// Espelha a whitelist do backend (asset-documents.service.ts::ALLOWED_MIME_TYPES).
// Se algo mudar aqui, mudar lá também — senão o front rejeita algo que o back aceitaria.
const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'application/xml',
  'text/xml',
]);
const ACCEPT_ATTR = Array.from(ALLOWED_MIME_TYPES).join(',');
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export function AtivoAnexosTab({ assetId, canWrite }: Props) {
  const documentsQuery = useDocumentos(assetId);
  const uploadMutation = useUploadDocumento(assetId);
  const downloadMutation = useDownloadDocumento(assetId);
  const excluirMutation = useExcluirDocumento(assetId);
  const [selectedType, setSelectedType] = useState<AssetDocumentType>('invoice');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; fileName: string } | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    // 1) Mime — espelha a whitelist do backend, evita round-trip que retornaria 400.
    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      setError(
        file.type
          ? `Tipo de arquivo não permitido (${file.type}). Aceitos: PDF, PNG, JPG, XML.`
          : 'Não foi possível identificar o tipo do arquivo. Aceitos: PDF, PNG, JPG, XML.',
      );
      return;
    }
    // 2) Tamanho
    if (file.size > MAX_SIZE_BYTES) {
      setError('Arquivo excede o tamanho máximo de 10 MB');
      return;
    }
    setError(null);
    try {
      await uploadMutation.mutateAsync({ file, type: selectedType });
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      let msg = 'Falha ao enviar arquivo';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.join(', ');
      setError(msg);
    }
  }

  async function handleDownload(documentId: string) {
    try {
      await downloadMutation.mutateAsync(documentId);
    } catch {
      setError('Falha ao baixar documento');
    }
  }

  async function handleDelete(documentId: string) {
    setPendingDelete(null);
    try {
      await excluirMutation.mutateAsync(documentId);
    } catch {
      setError('Falha ao remover anexo');
    }
  }

  return (
    <div className="space-y-4">
      {canWrite && (
        <div>
          <div className="mb-3 w-full max-w-xs">
            <AutocompleteSelect
              label="Tipo do documento"
              options={(Object.entries(DOCUMENT_TYPE_LABELS) as Array<[AssetDocumentType, string]>).map(([k, v]) => ({ value: k, label: v }))}
              value={selectedType}
              onChange={(v) => setSelectedType(v as AssetDocumentType)}
            />
          </div>
          <div
            className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFiles(e.dataTransfer.files); }}
          >
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Arraste um arquivo aqui ou{' '}
              <label className="cursor-pointer text-blue-600 hover:underline">
                selecione do computador
                <input
                  type="file"
                  className="hidden"
                  accept={ACCEPT_ATTR}
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </label>
            </p>
            <p className="mt-1 text-xs text-gray-500">PDF, PNG, JPG ou XML — até 10 MB.</p>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          {uploadMutation.isPending && <p className="mt-2 text-sm text-gray-600">Enviando...</p>}
        </div>
      )}

      {documentsQuery.isLoading ? (
        <div className="animate-pulse space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (<div key={i} className="h-12 rounded bg-gray-100" />))}
        </div>
      ) : documentsQuery.data?.data.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-6">Nenhum anexo enviado.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {documentsQuery.data?.data.map((doc) => (
            <li key={doc.id} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{doc.fileName}</p>
                  <p className="text-xs text-gray-500">
                    {DOCUMENT_TYPE_LABELS[doc.type]} · {formatarTamanho(doc.fileSize)} · {formatarData(doc.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleDownload(doc.id)}
                  className="rounded p-1.5 text-gray-500 hover:bg-gray-100" title="Baixar">
                  <Download className="h-4 w-4" />
                </button>
                {canWrite && (
                  <button
                    onClick={() => setPendingDelete({ id: doc.id, fileName: doc.fileName })}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50"
                    title="Remover"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remover anexo"
        message={pendingDelete ? `Remover "${pendingDelete.fileName}"? O arquivo deixa de aparecer, mas fica arquivado no storage.` : ''}
        confirmLabel="Remover"
        danger
        busy={excluirMutation.isPending}
        onConfirm={() => pendingDelete && handleDelete(pendingDelete.id)}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
